import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '@/widgets';

export interface ConfirmationConfig {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'destructive';
  onConfirm: () => Promise<void> | void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  className?: string;
}

export function useConfirmationModal(): {
  openModal: (config: ConfirmationConfig) => void;
  closeModal: () => void;
  Modal: React.ReactElement | null;
} {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState<ConfirmationConfig | null>(null);

  const openModal = useCallback((confirmationConfig: ConfirmationConfig) => {
    setConfig(confirmationConfig);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setIsLoading(false);
    // Don't clear config immediately - let the animation complete first
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!config?.onConfirm) return;

    try {
      setIsLoading(true);
      await config.onConfirm();
      closeModal();
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [config, closeModal]);

  const handleCancel = useCallback(() => {
    if (config?.onCancel) {
      config.onCancel();
      return;
    }
    closeModal();
  }, [closeModal]);

  // Handle animation end to clear config when slide-out animation completes
  const handleAnimationEnd = useCallback(
    (event: React.AnimationEvent<HTMLDivElement>) => {
      // Only clear config if it's the slide-out animation and modal is closed
      if (event.animationName.includes('slide-out') && !isOpen) {
        setConfig(null);
      }
    },
    [isOpen]
  );

  const Modal = useMemo(() => {
    if (!config) return null;

    const confirmationText = config.confirmText || t('common.delete');
    const cancelText = config.cancelText || t('common.cancel');
    const confirmVariant = config.confirmVariant;
    const title = config.title || '';
    const description = config.description || '';

    return (
      <>
        <ConfirmationModal
          isOpen={isOpen}
          onClose={closeModal}
          onConfirm={handleConfirm}
          title={title}
          description={description}
          confirmText={confirmationText}
          cancelText={cancelText}
          confirmVariant={confirmVariant}
          isLoading={isLoading}
          showCancelButton={config?.showCancelButton}
          onAnimationEnd={handleAnimationEnd}
          className={config.className}
        />
      </>
    );
  }, [
    isOpen,
    isLoading,
    config,
    handleConfirm,
    handleCancel,
    closeModal,
    handleAnimationEnd,
    t,
  ]);

  return {
    openModal,
    closeModal,
    Modal,
  };
}
